import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { AuthShell } from "../components/AuthShell";
import { useTranslation } from "../i18n";
import { Alert, Button, LoadingBlock } from "../components/ui";
import { ApiError, api } from "../lib/api";
import { formatMoney } from "../lib/money";

type State =
  | { kind: "working" }
  | {
      kind: "done";
      alreadyVerified: boolean;
      /**
       * The campaign a signup code paid out, described for the success line.
       *
       * Null covers both "no code was entered" and "one was and it was refused".
       * The refusal is deliberately not shown here: somebody who has just
       * confirmed their address should be told they can sign in, not handed an
       * error about an offer they can retry from the billing page.
       */
      promotion: string | null;
      /**
       * The campaign a signup applied to, named for the same reason.
       *
       * Confirming the address is what files the application -- it is not filed
       * at registration, so that a signup which never reads its own email
       * cannot take a place in the queue. That makes this screen the only
       * moment the applying is acknowledged, and saying nothing here leaves
       * somebody who ticked a box two screens ago with no evidence it counted.
       *
       * Null covers every ordinary case: nobody applied, or the campaign ended
       * while they were reading their email. Like the promotion above, the
       * refusal is not surfaced -- somebody confirming an address should be
       * told they can sign in, not handed an error about an offer.
       */
      application: string | null;
    }
  | { kind: "failed"; message: string; canResend: boolean };

/**
 * Where a confirmation link lands.
 *
 * It reads the token out of the query string and posts it, rather than the link
 * being a GET that verifies on its own. Two reasons, and the second is the one
 * that matters: a GET that changes state is opened by every mail scanner and
 * link prefetcher between the platform and the customer, and a browser may
 * re-issue it on a refresh. The API tolerates that — a second redemption reports
 * success — but the token would still be spent by something that is not a
 * person, and a plain GET makes that the normal case rather than the odd one.
 *
 * The token is stripped from the URL as soon as it has been read, so it does not
 * sit in history, in a bookmark, or in the `Referer` of anything this page later
 * links to.
 */
export function VerifyEmailPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation("auth");
  const token = searchParams.get("token");
  const [state, setState] = useState<State>(() =>
    token
      ? { kind: "working" }
      : { kind: "failed", message: t("verifyEmail.incomplete"), canResend: true },
  );

  /*
   * React runs effects twice in development StrictMode, and this one spends a
   * single-use token. The API answers the second call with success rather than
   * an error, so the guard is not load-bearing for correctness — it is here so
   * the network tab shows what actually happened once.
   */
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;

    // Out of the URL before anything else. The request already has it.
    const next = new URLSearchParams(searchParams);
    next.delete("token");
    setSearchParams(next, { replace: true });

    void (async () => {
      try {
        const result = await api<{
          verified: true;
          alreadyVerified: boolean;
          promotion: { campaign: { name: string }; creditedMicros: number } | null;
          freeInference: { name: string } | null;
        }>("/v1/auth/verify-email", { method: "POST", body: { token }, anonymous: true });

        setState({
          kind: "done",
          alreadyVerified: result.alreadyVerified,
          /*
           * Named with its amount where there is one. A match campaign credits
           * nothing now — it applies to the next payment — so naming the offer
           * without an amount is the true statement for that case.
           */
          promotion: result.promotion
            ? result.promotion.creditedMicros > 0
              ? t("verifyEmail.promotionFrom", {
                  amount: formatMoney(result.promotion.creditedMicros, "USD"),
                  campaign: result.promotion.campaign.name,
                })
              : result.promotion.campaign.name
            : null,
          application: result.freeInference?.name ?? null,
        });
      } catch (cause) {
        setState({
          kind: "failed",
          message:
            cause instanceof ApiError ? cause.message : t("verifyEmail.failed"),
          // Every failure here is answered by asking for a fresh link, so the
          // button is offered on all of them rather than only on "expired".
          canResend: true,
        });
      }
    })();
  }, [token, searchParams, setSearchParams, t]);

  return (
    <AuthShell
      title={state.kind === "done" ? t("verifyEmail.confirmed") : t("verifyEmail.confirming")}
      subtitle={state.kind === "done" ? t("verifyEmail.signInToStart") : undefined}
      footer={
        state.kind === "done" ? undefined : (
          <>
            {t("verifyEmail.needLink")}{" "}
            <Link
              className="text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
              to="/login"
            >
              {t("verifyEmail.signIn")}
            </Link>
          </>
        )
      }
    >
      <div className="flex flex-col items-center gap-5 text-center">
        {state.kind === "working" && <LoadingBlock label={t("verifyEmail.confirming")} />}

        {state.kind === "done" && (
          <>
            {/* The title already says confirmed; only an extra fact earns a line. */}
            {state.alreadyVerified && <Alert tone="success">{t("verifyEmail.alreadyConfirmed")}</Alert>}
            {!state.alreadyVerified && state.promotion && (
              <Alert tone="success">{t("verifyEmail.promotionApplied", { promotion: state.promotion })}</Alert>
            )}
            {/* What happens next, not what just happened: the application is
                filed and a person decides on it, so the one thing worth saying
                is that nothing more is required of them. */}
            {!state.alreadyVerified && state.application && (
              <Alert tone="success">
                {t("verifyEmail.applied", { name: state.application })}
              </Alert>
            )}
            <Link to="/login">
              <Button>{t("verifyEmail.signIn")}</Button>
            </Link>
          </>
        )}

        {state.kind === "failed" && (
          <>
            <Alert>{state.message}</Alert>
            {state.canResend && (
              <p className="text-sm leading-6 text-fg-muted">{t("verifyEmail.getNewLink")}</p>
            )}
            <Link to="/login">
              <Button variant="outline">{t("verifyEmail.signIn")}</Button>
            </Link>
          </>
        )}
      </div>
    </AuthShell>
  );
}
